import { useState } from 'react';
import { api, ApiFailure } from '../lib/api';
import { money } from '../lib/money';

export interface CodOutstanding {
  id: string;
  orderNo: string;
  receiverName: string;
  customerName: string | null;
  codAmount: string;
  codPending: boolean;
  currency: string;
  deliveredAt: string | null;
}

export interface CodHandoverState {
  due: boolean;
  count: number;
  truncated: boolean;
  totalAmount: string;
  anyPending: boolean;
  currency: string | null;
  orders: CodOutstanding[];
}

/**
 * 「别忘了把代收的现金交给店里」。
 *
 * ★ 为什么值得占满整个屏幕:这是**司机身上的现金**。
 *   忘了不会有任何系统提示 —— 店家发现是对账那天,
 *   而那时他已经说不清这钱在不在身上、是不是花掉了。
 *   一条塞在角落的横幅在一天几十次的操作里等于不存在。
 *
 * ★ 两个按钮,没有第三个,也没有右上角那个 ✕。
 *   点空白关掉、按返回关掉,在这一件事上都等于「我当没看见」——
 *   而它要问的是一个只有两种答案的问题:给了,还是还没给。
 */
export function CodHandoverDialog({
  state,
  onLater,
  onDone,
}: {
  state: CodHandoverState;
  /** 下次提醒 —— 什么都不记,下次打开还问 */
  onLater: () => void;
  /** 已经给了 —— 标掉之后刷新上层 */
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('/driver/cod-handover/ack', {
        method: 'POST',
        /*
         * ★ 提交的是**这一屏上真的列出来的那几单**,不是一句「全清」。
         *   司机看着这个列表把钱数给店里,他确认的就是这几单。
         *   后端按 id 标,晚到的那一单下次照常出现(见 ackCodHandover)。
         */
        body: { orderIds: state.orders.map((o) => o.id) },
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '没记上,再试一次');
      setBusy(false);
    }
  };

  const cur = state.currency ?? '';

  return (
    <div className="fixed inset-0 z-[60] flex items-end bg-black/60">
      <div
        className="w-full max-h-[90dvh] overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-800
                   p-4 pb-safe"
        role="dialog"
        aria-modal="true"
        aria-label="代收现金提醒"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          别忘了把代收的现金交给店里
        </h3>

        {/*
          ★ 先给总数和总额,再给明细。
            司机要做的第一件事是数钱 —— 那一步只需要一个数字;
            明细是给「对不上的时候一单一单核」用的,不该挡在前面。
        */}
        <div
          className="mt-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200
                     dark:border-amber-900 px-3 py-3"
        >
          {/*
            ★ 写清「昨天及之前」,不写成「还有几单」。
              服务端只算当天零点以前送达的单(今天刚收的等明天再问),
              这里不说的话,他会拿这个数去对**今天全部**的现金 ——
              对不上,然后不再信这个弹窗。
          */}
          <div className="text-sm text-amber-800 dark:text-amber-200">
            昨天及之前还有 <span className="font-semibold tabular-nums">{state.count}</span>{' '}
            单的现金在你手上
          </div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-amber-900 dark:text-amber-100">
            {money(state.totalAmount, cur)}
            {/*
              ★ 金额待定的单不进这个合计,而且必须说出来。
                那种单是司机到场前跟商家确认收多少的,库里存的是 0 ——
                闷声算进去等于告诉他「就差这么多」,而实际差得更多。
            */}
            {state.anyPending && (
              <span className="ml-2 align-middle text-sm font-normal text-amber-700 dark:text-amber-300">
                另有金额待定的单,实收以商家为准
              </span>
            )}
          </div>
        </div>

        <ul className="mt-3 space-y-2">
          {state.orders.map((o) => (
            <li
              key={o.id}
              className="flex items-center justify-between gap-3 rounded-xl border
                         border-slate-200 dark:border-slate-700 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-slate-900 dark:text-slate-100">
                  {o.receiverName}
                  {o.customerName && (
                    <span className="ml-2 text-xs text-slate-400">{o.customerName}</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 tabular-nums">{o.orderNo}</div>
              </div>
              <div className="shrink-0 tabular-nums text-slate-900 dark:text-slate-100">
                {o.codPending ? (
                  <span className="text-sm text-amber-600 dark:text-amber-400">待定</span>
                ) : (
                  money(o.codAmount, o.currency)
                )}
              </div>
            </li>
          ))}
        </ul>

        {/*
          ★ 截断了必须说。按列表交完就以为清空了,而库里还欠着几十单 ——
            这是钱,不能让界面制造「已经清完」的错觉。
        */}
        {state.truncated && (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            只列了前 {state.orders.length} 单,确认后剩下的会接着提醒
          </p>
        )}

        {error && (
          <div
            className="mt-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                       dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <div className="mt-4 flex gap-3">
          <button
            onClick={onLater}
            disabled={busy}
            className="h-12 flex-1 rounded-xl border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-200 disabled:opacity-50"
          >
            下次提醒
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="h-12 flex-1 rounded-xl bg-brand-500 font-medium text-white
                       disabled:opacity-50"
          >
            {busy ? '记录中…' : '已经给了'}
          </button>
        </div>
        {/*
          ★ 把「已经给了」这一下的后果写出来。
            它一次标掉上面所有单,而司机默认会以为这只是关掉一个提示。
        */}
        <p className="mt-2 text-center text-xs text-slate-400">
          点「已经给了」表示上面这些单的现金都交给店里了
        </p>
      </div>
    </div>
  );
}
